/**
 * Admin API keys — the credentials MAD and other external systems use.
 *
 * ─── The key is shown once, and this is the only place it exists ────────────
 * The backend stores a SHA-256 hash and an eight-character prefix. `POST` is
 * the one and only response that carries the full key; nothing can produce it
 * again, including the backend. So the created key is pinned to the top of the
 * tab until it is explicitly dismissed, rather than dropped into the notice bar
 * with the other transient messages — a notice that a later action clears
 * would take an unrecoverable secret with it.
 *
 * It is deliberately not auto-copied to the clipboard: a silent clipboard write
 * is a poor way to hand someone a credential they may not have been ready for.
 */
import { createApiKey, deleteApiKey, listApiKeys } from '../admin-api';
import type { ApiKeyRow, CreatedApiKey } from '../admin-api.types';
import { errorText, esc, field, fmtDate, readForm, section, table } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

export const apiKeysTab: AdminTab = {
  id: 'api-keys',
  label: 'API keys',
  group: 'Access',

  mount(host, ctx) {
    let live = true;
    let rows: ApiKeyRow[] | null = null;
    let error = '';
    let created: CreatedApiKey | null = null;

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!rows) return '<p class="tg-adm-empty">Loading…</p>';

      const secret = created
        ? `<div class="tg-adm-drawer">
             <h3 class="tg-adm-label">${esc(created.label)} — copy this now</h3>
             <p class="tg-adm-mono tg-adm-secret">${esc(created.key)}</p>
             <p class="tg-adm-hint">Only the hash is stored. Once this is dismissed it cannot be
               shown again by anyone, including the server.</p>
             <div class="tg-adm-actions">
               <button type="button" class="tg-adm-btn" data-act="dismiss-key">I have copied it</button>
             </div>
           </div>`
        : '';

      return `
        ${secret}
        <form class="tg-adm-form" data-form="create">
          <div class="tg-adm-form-row">
            ${field('label', 'New key label', '', { placeholder: 'MAD dashboard' })}
            <div class="tg-adm-actions">
              <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Create key</button>
            </div>
          </div>
        </form>
        ${table(
    rows,
    [
      { label: 'Label', cell: (r) => `<strong>${esc(r.label)}</strong>` },
      { label: 'Prefix', cell: (r) => `<span class="tg-adm-mono">${esc(r.prefix)}…</span>`, tight: true },
      {
        label: 'Created by',
        cell: (r) => esc(r.createdBy?.username || r.createdBy?.email || '—'),
        tight: true,
      },
      { label: 'Created', cell: (r) => esc(fmtDate(r.createdAt)), tight: true },
      {
        label: 'Last used',
        cell: (r) => (r.lastUsedAt
          ? esc(fmtDate(r.lastUsedAt))
          : '<span class="tg-adm-hint">never</span>'),
        tight: true,
      },
      {
        label: '',
        tight: true,
        cell: (r) => `<button type="button" class="tg-adm-btn tg-adm-btn-danger"
          data-act="revoke" data-id="${esc(r.id)}" data-label="${esc(r.label)}">Revoke</button>`,
      },
    ],
    'No API keys issued.',
  )}`;
    }

    const paint = () => {
      host.innerHTML = section(
        'API keys',
        'Credentials for MAD and other external admin systems. They authenticate as ADMIN.',
        body(),
      );
    };

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await listApiKeys();
        if (!live) return;
        rows = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
      if (el?.dataset.act === 'dismiss-key') { created = null; paint(); return; }
      if (el?.dataset.act !== 'revoke') return;
      const id = el.dataset.id!;
      ctx.confirm({
        title: `Revoke "${el.dataset.label}"?`,
        body: 'Any system still using this key starts getting 401s immediately.',
        confirmLabel: 'Revoke',
        onConfirm: () => void (async () => {
          try {
            await deleteApiKey(id);
            if (!live) return;
            ctx.notify('Key revoked.');
          } catch (err) {
            if (live) ctx.notify(errorText(err), 'err');
          }
          await load();
        })(),
      });
    };

    const onSubmit = (e: SubmitEvent) => {
      const formEl = e.target as HTMLFormElement;
      if (formEl.dataset.form !== 'create') return;
      e.preventDefault();
      const label = String(readForm(formEl).label || '').trim();
      if (!label) { ctx.notify('Give the key a label so it can be recognised later.', 'err'); return; }
      void (async () => {
        try {
          created = await createApiKey(label);
          if (!live) return;
          ctx.notify('Key created — copy it before leaving this tab.');
        } catch (err) {
          if (live) ctx.notify(errorText(err), 'err');
          return;
        }
        await load();
      })();
    };

    host.addEventListener('click', onClick);
    host.addEventListener('submit', onSubmit as EventListener);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('submit', onSubmit as EventListener);
    };
  },
};
